import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { postRentalVendorForm } from '../../../api/rentals';
import { AwaitButton } from '../../../reusable-components/await-button';
import CloseIconButton from '../../../reusable-components/basic-buttons/CloseIconButton';
import DropzoneImage from '../../../reusable-components/DropzoneImage/DropzoneImage';
import { useSnackbar } from '../../../reusable-components/snackbar';

RentalVendorForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSaved: PropTypes.func,
  row: PropTypes.object,
};

export default function RentalVendorForm({ open, onClose, onSaved, row }) {
  const { enqueueSnackbar } = useSnackbar();
  const [value, setValue] = useState({});

  useEffect(() => {
    setValue({
      id: row?.id ?? 0,
      name: row?.name ?? '',
      emailAddress: row?.emailAddress ?? '',
      address: row?.address ?? '',
      city: row?.city ?? '',
      state: row?.state ?? '',
      zip: row?.zip ?? '',
      phone: row?.phone ?? '',
      logo: row?.logo ?? '',
    });
  }, [open, row]);

  const updateField = (field, fieldValue) => setValue((current) => ({ ...current, [field]: fieldValue }));

  const handleSave = async () => {
    try {
      const response = await postRentalVendorForm(value);
      if (!response?.success) {
        enqueueSnackbar(response?.message || 'Unable to save rental vendor.', { variant: 'error' });
        return;
      }
      enqueueSnackbar('Rental vendor saved successfully.', { variant: 'success' });
      onClose();
      await onSaved();
    } catch {
      enqueueSnackbar('Unable to save rental vendor.', { variant: 'error' });
    }
  };

  const formFields = [
    ['name', 'Name'],
    ['emailAddress', 'Email Address'],
    ['address', 'Address'],
    ['city', 'City'],
    ['state', 'State'],
    ['zip', 'Zip'],
    ['phone', 'Phone'],
  ];

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ textAlign: 'center' }}>
        {row ? 'Edit' : 'Add'} Rental Vendor
        <CloseIconButton onClick={onClose} />
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {formFields.map(([field, label]) => (
            <Grid item xs={12} sm={field === 'address' || field === 'name' || field === 'emailAddress' ? 12 : 6} key={field}>
              <Box>
                <Typography variant="subtitle1">{label}{field === 'name' ? '*' : ''}</Typography>
                <TextField
                  autoComplete="off"
                  fullWidth
                  size="small"
                  value={value[field] ?? ''}
                  onChange={(event) => updateField(field, event.target.value)}
                />
              </Box>
            </Grid>
          ))}
          <Grid item xs={12}>
            <DropzoneImage
              value={value.logo}
              setValue={(logo) => setValue((current) => ({ ...current, logo }))}
              label="Vendor Logo"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={onClose}>
          Cancel
        </Button>
        <AwaitButton variant="contained" color="primary" onClick={handleSave} disabled={!value.name?.trim()}>
          {row ? 'Save' : 'Add Vendor'}
        </AwaitButton>
      </DialogActions>
    </Dialog>
  );
}
